/**
 * shoppingModeStyles — token-only inline style objects for the Shopping list
 * (T12.1.5.1.7). Mirrors the prototype's `#s-list-shop` look: store-grouped, fat
 * single-tap completion rows, hide-completed with a visible remaining count.
 * No hex, no raw pixel literals (CP5). Touch-first: the check row clears
 * 2.75rem (44+) and the box is 1.625rem (26), matching the prototype. The
 * prototype reference comments below cite its hand-tuned sizes as bare numbers.
 *
 * Bounded to the prototype's thin depth — no aisle reordering, store-progress
 * nodes, loyalty-at-till, next-stop, or scan. Aisle grouping is NOT built (no
 * aisle data); we group by selected store, with a single flat group when the
 * store is unknown.
 *
 * The same components render at both widths; desktop gets the centered
 * max-width column from the shell wrapper, not different rows.
 */

import type { CSSProperties } from 'react';

export const pageWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  // The "Show purchased" toggle is the last element; on a short list it ends
  // up near the fixed bottom nav. The shared shell reserves ~80px below `main`,
  // but the prototype's scroll body reserves ~96px — add the surface's own
  // bottom reserve so the toggle (and the last list item) stay fully tappable
  // above the fixed bottom nav at 390px. Local to Shopping; no shell change.
  paddingBottom: 'var(--space-6)',
};

export const titleStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — "Shopping" page heading → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
};

export const subStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  margin: 'var(--space-1) 0 var(--space-3)',
};

// One passive nudge slot — calm by default (a glance reads as a list, not ads).
// The prototype paints a warm amber surface; CP5 has no amber-tint token, so we
// use the muted surface + subtle border and let the 🏷️ glyph carry the warmth.
export const nudgeStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  alignItems: 'center',
  backgroundColor: 'var(--color-bg-muted)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3h) var(--space-3q)',
  margin: 'var(--space-1) 0 var(--space-3)',
};

export const nudgeIconStyle: CSSProperties = {
  fontSize: 'var(--font-size-md)',
  flex: '0 0 auto',
};

export const nudgeTextStyle: CSSProperties = {
  // Prototype nudge body: 12.5.
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-secondary)',
  flex: 1,
  minWidth: 0,
};

// Per-store group + its header (prototype `.storeGroup` / `.storeHead`).
// T41 fix — `space-4` so groups read as section breaks; the backgroundless
// header `margin: 0` binds it tight to its own items.
export const storeGroupStyle: CSSProperties = {
  marginBottom: 'var(--space-4)',
};

// T41 — the store-group header is a full-width foldable `<button>` (≥44px)
// carrying the name + branch label, the always-visible count, and a chevron.
// Press/focus/hover come from the global `button:active` / `button:focus-visible`
// rules in responsive.css — inline styles can't express pseudo-classes, and the
// header is a native `<button>`.
// T41 fix — backgroundless: no card surface, border, or shadow. The header reads
// as a section heading bound to its items (the items below own the card chrome).
// A small radius keeps the focus-ring corners intentional.
export const storeHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  width: '100%',
  minHeight: '2.75rem',
  margin: 0,
  padding: 'var(--space-2) var(--space-1)',
  boxSizing: 'border-box',
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'var(--font-family-sans)',
  color: 'inherit',
};

// The name + branch label column (prototype `.storeHead .hgrp`). T41 fix — a
// column so the branch sits on its own line under the name; the count + chevron
// stay right-aligned beside the column.
export const storeHeadLabelGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  minWidth: 0,
  flex: 1,
};

export const storeHeadNameStyle: CSSProperties = {
  // T41 fix — heading weight + size, truncates with an ellipsis when narrow.
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-base15)',
  color: 'var(--color-text-primary)',
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
};

// T41 — the branch/street locator under the name (prototype `.storeHead .loc`,
// e.g. "Bank St") that disambiguates two same-chain locations. T41 fix — its own
// line, muted, regular weight; absent on the unassigned "grab anywhere" group.
export const storeHeadBranchStyle: CSSProperties = {
  display: 'block',
  fontWeight: 'var(--font-weight-regular)',
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-muted)',
  lineHeight: 'var(--line-height-tight)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
};

export const storeHeadCountStyle: CSSProperties = {
  // Prototype `.storeHead .c`: 12 — the always-visible per-store remaining count.
  fontSize: 'var(--font-size-3xs)',
  color: 'var(--color-text-muted)',
  marginLeft: 'auto',
  flex: '0 0 auto',
  fontWeight: 'var(--font-weight-semibold)',
  whiteSpace: 'nowrap',
};

// T41 — the fold chevron (prototype `.storeHead .fold`). Rotates to point right
// when the group is collapsed (`storeHeadChevronCollapsedStyle`). Honors
// `prefers-reduced-motion` via the responsive.css `data-testid` rule (transition
// is dropped there); the inline transform is instant and safe.
export const storeHeadChevronStyle: CSSProperties = {
  flex: '0 0 auto',
  marginLeft: 'var(--space-1)',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-base)',
  display: 'inline-flex',
  transition: 'transform 0.2s ease',
};

export const storeHeadChevronCollapsedStyle: CSSProperties = {
  ...storeHeadChevronStyle,
  transform: 'rotate(-90deg)',
};

// T41 — the items container under a store header. Carries a stable `id` so the
// header's `aria-controls` and the switcher's `scrollIntoView` target it.
export const storeItemsStyle: CSSProperties = {
  display: 'block',
};

// T41 — the sticky store switcher chip row (prototype `.storeSwitch`). Pins
// above the groups, just below the app header. The window/body is the scroll
// container (AppShell `<main>` has no overflow) and the app header is itself
// `position: sticky; top: 0`, so a plain `top: 0` here would ride UP OVER the
// header. `top` offsets by the live header height (`--app-header-height`,
// published by AppShell from the sticky header's real height incl. the native
// status-bar inset) so the switcher tucks JUST BELOW the header — same pattern
// as the Deals sticky cluster. `--z-sticky` matches Deals' sticky cluster, so
// the header always stacks at/above the switcher (it tucks under, never over).
// Plain chips, horizontally scrollable when many; NO status color (deferred to
// F23). Press/focus come from the global `button:active`/`button:focus-visible`
// rules in responsive.css (chips are native `<button>`s).
export const storeSwitchStyle: CSSProperties = {
  position: 'sticky',
  top: 'var(--app-header-height, 0px)',
  // `--z-sticky` is a CSS var; React's CSSProperties types `zIndex` as number, so
  // cast (same pattern as FeatureIdeasSection's sticky sort bar).
  zIndex: 'var(--z-sticky)' as unknown as number,
  display: 'flex',
  gap: 'var(--space-2)',
  overflowX: 'auto',
  padding: 'var(--space-2) 0',
  margin: '0 0 var(--space-3)',
  backgroundColor: 'var(--color-bg-primary)',
  // T41 fix — a right edge-fade hints the row scrolls past the viewport when
  // chips overflow. `black`/`transparent` are mask on/off keywords (theme-
  // identical); the `black` keyword avoids token-scan's hex flag.
  WebkitMaskImage:
    'linear-gradient(to right, black calc(100% - 2rem), transparent 100%)',
  maskImage:
    'linear-gradient(to right, black calc(100% - 2rem), transparent 100%)',
};

// One switcher chip (prototype `.swChip`). ≥44px tap, plain — no status tint.
export const storeSwitchChipStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  padding: 'var(--space-1) var(--space-3)',
  backgroundColor: 'var(--color-bg-raised)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-pill)',
  color: 'var(--color-text-secondary)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-2sm)',
  fontWeight: 'var(--font-weight-semibold)',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

// T41 — the store name inside a switcher chip — truncates so a long name can't
// blow the chip out of the row.
export const storeSwitchChipNameStyle: CSSProperties = {
  maxWidth: '11rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// The branch label inside a switcher chip — muted, follows the name.
export const storeSwitchChipBranchStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontWeight: 'var(--font-weight-regular)',
};

// Fat single-tap completion row (prototype `.check`). 44+ target; one tap
// anywhere on the row toggles completion.
export const checkRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  width: '100%',
  boxSizing: 'border-box',
  // The outer card shape (border + radius + raised surface) lives on
  // `checkRowWrapStyle` (which clips overflow), mirroring how Planning wraps the
  // header + expanded block in ONE bordered card. The check row itself is
  // border-free so the row and the expanded panel share one continuous edge with
  // no doubled/mismatched seam and no width overflow.
  // Prototype `.check`: 14 padding, 44 min-height.
  padding: 'var(--space-3q)',
  minHeight: '2.75rem',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'inherit',
  fontFamily: 'var(--font-family-sans)',
};

// The row-open button — everything right of the ✓ box (thumb · name · price).
// Tapping it opens the expanded view; it never completes the item. Fills the
// remaining row width and resets the native button chrome.
export const checkRowOpenStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  flex: 1,
  minWidth: 0,
  minHeight: '2.75rem',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'inherit',
  fontFamily: 'var(--font-family-sans)',
  padding: 0,
};

// The big completion box (prototype `.box`): 26, the checkmark indicator. Its
// tap target is `boxButtonStyle` (the wrapping button); this is the visual fill.
export const boxStyle: CSSProperties = {
  width: '1.625rem',
  height: '1.625rem',
  borderRadius: 'var(--radius-lg)',
  borderWidth: '0.125rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  color: 'var(--color-text-inverse)',
  // Prototype `.box`: 15 checkmark.
  fontSize: 'var(--font-size-base15)',
  backgroundColor: 'transparent',
};

export const boxDoneStyle: CSSProperties = {
  ...boxStyle,
  backgroundColor: 'var(--color-status-success)',
  borderColor: 'var(--color-status-success)',
};

// Per-item emoji thumbnail. The prototype's shopping thumb is 32px
// (`.check .thumb{width:32px;height:32px}`) with an 18px glyph — SMALLER than
// the collapsed Planning row's thumb, keeping the in-store rows compact and
// aligned. Sits between the completion box and the name: [box] [thumb] [name]
// [price].
export const thumbStyle: CSSProperties = {
  width: '2rem',
  height: '2rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-md)',
  flex: '0 0 auto',
};

export const checkMetaStyle: CSSProperties = {
  flex: 1,
  // F4 (T27.2.5.5) — mirror the Planning row's protected name column so the
  // shopping item name stays legible at 360px and the trailing price gives way
  // first (price already `flex: 0 0 auto`).
  minWidth: '8rem',
};

export const checkNameStyle: CSSProperties = {
  // Prototype `.check .nm` inherits `.nm` (15).
  display: 'block',
  fontSize: 'var(--font-size-base15)',
  fontWeight: 'var(--font-weight-semibold)',
  lineHeight: 'var(--line-height-tight)',
};

export const checkNameDoneStyle: CSSProperties = {
  ...checkNameStyle,
  textDecoration: 'line-through',
  color: 'var(--color-text-muted)',
  fontWeight: 'var(--font-weight-regular)',
};

// The collapsed secondary line (`bsz` in the prototype): `brand · size · qtyedit
// [· on sale] · notedot`. One nowrap line under the name; truncates with an
// ellipsis when narrow (prototype `.check .bsz`: 12, single line). The name line
// (`checkNameStyle`) carries the item name ONLY — the brand lives here, not there.
export const checkBszStyle: CSSProperties = {
  // Prototype `.check .bsz`: 12.
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 'var(--space-1)',
  fontSize: 'var(--font-size-2xs)',
  color: 'var(--color-text-secondary)',
  marginTop: 'var(--space-1)',
  lineHeight: 'var(--line-height-tight)',
};

export const checkBszDoneStyle: CSSProperties = {
  ...checkBszStyle,
  color: 'var(--color-text-muted)',
};

// The "on sale" flag inside the `bsz` line (prototype `.check .bsz .sale`).
export const checkSaleStyle: CSSProperties = {
  color: 'var(--color-status-success)',
  fontWeight: 'var(--font-weight-bold)',
};

// The brand text on the `bsz` line — plain, inherits the line colour.
export const checkBrandStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// `sizeText` — the read-only package size ON the collapsed `bsz` line. Display
// only: a tap reaches the parent row-open button and opens the expanded view
// (settings is reachable only from the expanded view). Plain text consistent
// with the rest of the `bsz` line — no link affordance.
export const sizeTextStyle: CSSProperties = {
  flex: '0 0 auto',
};

// `notedot` — a small dot on the collapsed secondary line when a note exists
// (prototype `.check .bsz .notedot`, deliverable 3). Wordless, brand-tinted.
export const noteDotStyle: CSSProperties = {
  display: 'inline-block',
  width: '0.3125rem',
  height: '0.3125rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-accent-primary)',
  flex: '0 0 auto',
};

// `qtyedit` — the inline quantity quick tap-edit ON the collapsed `bsz` line
// (prototype `:1088`). Default shows `×N` (`qvStyle`); tapping reveals the −/+
// stepper (`qStepStyle`) in place. Opts out of the global 44px min-height (it's
// an inline control on a text line, not a full-height target).
export const qtyEditStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  minHeight: 0,
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
  whiteSpace: 'nowrap',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-2xs)',
  padding: 0,
};

// The `×N` value shown when the stepper is collapsed.
export const qvStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
};

// The inline −/+ stepper revealed when `qtyedit` is tapped (prototype `.qstep`).
export const qStepStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
};

export const qStepButtonStyle: CSSProperties = {
  width: '1.375rem',
  height: '1.375rem',
  minHeight: 0,
  borderRadius: 'var(--radius-sm)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  padding: 0,
  display: 'inline-grid',
  placeItems: 'center',
};

export const qStepValueStyle: CSSProperties = {
  minWidth: '0.75rem',
  textAlign: 'center',
  fontWeight: 'var(--font-weight-bold)',
};

export const checkPriceStyle: CSSProperties = {
  marginLeft: 'auto',
  fontWeight: 'var(--font-weight-bold)',
  // Prototype `.check .p`: 14.
  fontSize: 'var(--font-size-sm)',
  flex: '0 0 auto',
};

// T12.1.8.7 — wraps the completion button + an optional "View deal" link so the
// deal control is a SIBLING of the row button (never nested-interactive inside
// it). The button keeps the row's own bottom margin; the wrapper collapses to
// the button alone when no deal link is present.
export const checkRowWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  // Owns the outer card shape so the check row + the expanded panel are one
  // bordered, corner-clipping card (mirrors Planning's `cardStyle`). `overflow:
  // hidden` keeps the children's square corners inside the rounded card, and
  // makes child width-overflow impossible — the row and panel always share one
  // continuous edge. Carries the per-row bottom gap that previously sat on the
  // check row.
  backgroundColor: 'var(--color-bg-raised)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  overflow: 'hidden',
  marginBottom: 'var(--space-2)',
};

// "View deal" link on a flyer-added shopping row. Quiet, sale-tinted; tucked at
// the row's trailing-left under the name, a 44+ target. Reopens the exact added
// deal (`/deals?deal=<flyerdeal_id>`).
export const dealLinkStyle: CSSProperties = {
  alignSelf: 'flex-start',
  minHeight: '2.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  marginTop: 'calc(-1 * var(--space-1))',
  marginBottom: 'var(--space-2)',
  paddingLeft: 'var(--space-3q)',
  fontSize: 'var(--font-size-2sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-status-danger)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

// Mark-unavailable control — a trailing action INSIDE the shopping expanded
// view (prototype `.sd-actions` → `<button>Mark unavailable</button>`), shown
// only when the row is tapped open. Sets the item's `status` to `deferred` (the
// existing third status — no schema change), taking it out of the pending set
// when the shelf is empty. Quiet by default so it doesn't compete with the
// completion tap. The wrapper (`unavailableActionsStyle`) reserves a little top
// space above the byline.
export const unavailableActionsStyle: CSSProperties = {
  paddingTop: 'var(--space-2)',
};

export const unavailableButtonStyle: CSSProperties = {
  minHeight: '2.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  fontSize: 'var(--font-size-2sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  padding: 0,
};

// The opened-up view (`expandedView`) for a shopping row — revealed when the row
// (not the box) is tapped. Mirrors the Planning `expandedView` rhythm with the
// shopping tokens: a dashed top divider, the same quantity / note / where-to-buy
// / who-added rows, but Shopping-mode controls (a quick tap-edit quantity, a
// read-only note). Sits inside the row wrap, below the check button.
export const expandedViewStyle: CSSProperties = {
  // The wrapping card (`checkRowWrapStyle`) owns the outer border + radius + bg,
  // so the panel only draws the single dashed internal divider that separates it
  // from the check row above (like Planning's `expandedViewStyle`). No own side /
  // bottom borders, no bottom radius, no negative margin — the seam is one line.
  boxSizing: 'border-box',
  borderTopWidth: '0.0625rem',
  borderTopStyle: 'dashed',
  borderTopColor: 'var(--color-border-subtle)',
  padding: 'var(--space-2) var(--space-3q) var(--space-3)',
  backgroundColor: 'var(--color-bg-raised)',
};

// One labelled row inside the shopping expanded view. Only "Where" is labelled
// (prototype `.sd-row`/`.sd-k`); the note is a bare field above it (no label).
// A small uppercase label on the left (`sd-k`, fixed 72px), the value on the right.
export const expandedRowStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2h)',
  alignItems: 'baseline',
  // Prototype `.sd-row`: 4px 0.
  padding: 'var(--space-1) 0',
  color: 'var(--color-text-secondary)',
};

export const expandedLabelStyle: CSSProperties = {
  // Prototype `.sd-row .sd-k`: 10.5, fixed 72px column.
  fontSize: 'var(--font-size-3xs)',
  fontWeight: 'var(--font-weight-bold)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--color-text-muted)',
  flex: '0 0 4.5rem',
};

export const expandedValueStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

// `itemNote` read-only line in Shopping — a BARE field (no "Note" label, per the
// prototype's `.sd-note`), the per-trip note authored in Planning. A muted,
// italic block; the empty state reads a quiet placeholder. Quantity moved to the
// collapsed `qtyedit`, so there is no "How many" row in the shopping panel.
export const noteReadonlyStyle: CSSProperties = {
  display: 'block',
  // Prototype `.sd-note`: italic 13, muted surface, full width.
  fontStyle: 'italic',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--space-2) var(--space-2h)',
  margin: 'var(--space-1) 0',
  whiteSpace: 'pre-wrap',
  // Cap a long read-only note at ~5 lines then scroll, matching the editable
  // Planning textarea's auto-grow ceiling so the two surfaces read the same.
  // 5 lines of `font-size-xs` at the normal line-height + the block's own
  // vertical padding (`space-2` ×2). Token-only (CP5).
  maxHeight:
    'calc(var(--font-size-xs) * var(--line-height-normal) * 5 + var(--space-2) * 2)',
  overflowY: 'auto',
  boxSizing: 'border-box',
};

export const noteEmptyStyle: CSSProperties = {
  ...noteReadonlyStyle,
  color: 'var(--color-text-muted)',
};

// `itemFoot` — the who-added byline, last in the shopping expanded view. No
// fine-tune link in Shopping (Planning only); the byline shares the contract
// names with Planning.
export const itemFootStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  paddingTop: 'var(--space-2)',
};

export const addedByBylineStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  flex: 1,
  minWidth: 0,
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-muted)',
};

// The clickable completion box wrapper. The box is now its OWN tap target (it
// completes the item; the row tap opens the expanded view), so it needs the 44+
// floor and a button reset. Visual fill stays on `boxStyle` / `boxDoneStyle`.
export const boxButtonStyle: CSSProperties = {
  display: 'inline-grid',
  placeItems: 'center',
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  marginLeft: 'calc(-1 * var(--space-1))',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  flex: '0 0 auto',
  fontFamily: 'var(--font-family-sans)',
};

// "Show purchased (N)" toggle — line button, full width (prototype `.btn.line`).
export const purchasedToggleStyle: CSSProperties = {
  width: '100%',
  minHeight: '2.75rem',
  padding: 'var(--space-2) var(--space-3)',
  margin: 'var(--space-2) 0',
  backgroundColor: 'var(--color-bg-raised)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-secondary)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  cursor: 'pointer',
};

export const emptyCardStyle: CSSProperties = {
  textAlign: 'center',
  padding: 'var(--space-6) var(--space-4)',
  color: 'var(--color-text-muted)',
};

export const emptyEmojiStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
};

export const allDoneStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
  textAlign: 'center',
  padding: 'var(--space-4) 0',
};

export const errorStyle: CSSProperties = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
  marginTop: 'var(--space-2)',
};
